import { datadogRum } from '@datadog/browser-rum';
import intersection from 'lodash/intersection';

import { Plan } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import {
  useLeaderOrLicenseeLocations,
  useLicenseeStaffUsesReportsPicker,
} from '@/hooks/useLeaderOrLicenseeLocations';
import usePlanOperators from '@/hooks/usePlanOperators';

import Constants from 'constants/index';

import { env } from '@/env';

/** Debug helper for empty location results — logs locally in dev, sends DD action in prod */
const debugEmptyLocations = (
  context: string,
  details: Record<string, unknown>,
) => {
  if (env.REACT_APP_ENV === 'dev') {
    console.debug(`[usePlanLocations] Empty result for ${context}:`, details);
  }
  if (env.REACT_APP_ENV === 'prod') {
    datadogRum.addAction('plan_locations_empty', {
      context,
      ...details,
    });
  }
};

type PlanLocationFields = Pick<Plan, 'type'> &
  Partial<Pick<Plan, 'readonly' | 'ownerId' | 'ownerType'>>;

type GetLocationsByPlan = (plan: PlanLocationFields) => string[];

interface UsePlanLocationsResult {
  getLocationsByPlan: GetLocationsByPlan;
  isReady: boolean;
}

// TODO: Migrate to useSuspenseQuery in usePlanOperators so this hook never
// returns a loading state — components suspend instead. Requires adding
// <Suspense> boundaries around consumer trees.
export const usePlanLocations = (): UsePlanLocationsResult => {
  const { data: auth } = useAuth();
  const leaderOrLicenseeLocations = useLeaderOrLicenseeLocations();
  const licenseeStaffUsesReportsPicker = useLicenseeStaffUsesReportsPicker();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const allLocationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const { operators, isFetching } = usePlanOperators();

  const getLocationsByPlan = (plan: PlanLocationFields): string[] => {
    // SC plans always resolve to 00000 for licensee staff (Plans and Reports).
    if (
      userIsLicenseeStaff &&
      plan.ownerType === Constants.PLAN_OWNER_TYPE.SUPPORT_CENTER
    ) {
      return [Constants.SUPPORT_STAFF_LOCATION];
    }

    if (userIsLicenseeStaff && licenseeStaffUsesReportsPicker) {
      if (leaderOrLicenseeLocations.length === 0) {
        debugEmptyLocations('licensee staff in Reports', {
          reason: 'No active licensee location selected in picker',
          userId: auth?.userId,
        });
      }
      return leaderOrLicenseeLocations;
    }

    // Licensee users (not staff) use the picker on Plans and Reports.
    if (isLicenseeUser && !userIsLicenseeStaff) {
      if (leaderOrLicenseeLocations.length === 0) {
        debugEmptyLocations('licensee user', {
          reason: 'No active licensee location selected in picker',
          userId: auth?.userId,
        });
      }
      return leaderOrLicenseeLocations;
    }

    const isCompliance = plan.type === 'COMPLIANCE';
    const isReadonly = plan.readonly;

    let result: string[];
    let debugDetails: Record<string, unknown>;

    if (isCompliance || isReadonly) {
      result = allLocationsWithAtLeastTrainer.filter(
        loc => userIsLicenseeStaff || loc !== Constants.SUPPORT_STAFF_LOCATION,
      );
      debugDetails = {
        context: 'compliance/readonly plan',
        userId: auth?.userId,
        isCompliance,
        isReadonly,
      };
    } else {
      if (isFetching) return [];

      const ownerLocations =
        operators.find(op => op.id === plan.ownerId)?.locations ?? [];

      const roleBasedLocations =
        auth?.highestRoleLevel === Constants.USER_ROLES.TRAINER
          ? allLocationsWithAtLeastTrainer
          : intersection(ownerLocations, allLocationsWithAtLeastTrainer);

      const assignableLocations = roleBasedLocations.filter(
        loc => userIsLicenseeStaff || loc !== Constants.SUPPORT_STAFF_LOCATION,
      );

      if (plan.ownerType === Constants.PLAN_OWNER_TYPE.SUPPORT_CENTER) {
        result =
          assignableLocations.length > 0
            ? assignableLocations
            : [Constants.SUPPORT_STAFF_LOCATION];
        debugDetails = {
          context: 'SUPPORT_CENTER plan',
          planOwnerId: plan.ownerId,
          assignableLocations,
          ownerLocations,
          userTrainerLocations: allLocationsWithAtLeastTrainer,
        };
      } else {
        result = assignableLocations;
        debugDetails = {
          context: 'non-compliance plan',
          planOwnerId: plan.ownerId,
          planOwnerType: plan.ownerType,
          ownerLocations,
          userTrainerLocations: allLocationsWithAtLeastTrainer,
        };
      }
    }

    if (result.length === 0) {
      debugEmptyLocations('Falling back to leaderOrLicenseeLocations', {
        ...debugDetails,
        userLocations: leaderOrLicenseeLocations,
      });
      return leaderOrLicenseeLocations;
    }

    return result;
  };

  return { getLocationsByPlan, isReady: !isFetching };
};

export default usePlanLocations;
